'use client'
import { Close } from '@carbon/icons-react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { ModalContext } from 'lib/modalContext'
import type { ElementType, ReactNode } from 'react'
import React from 'react'
import { useFormStatus } from 'react-dom'
import { twMerge } from 'tailwind-merge'
import { Button } from './Button'

function Dialog(props: DialogPrimitive.DialogProps) {
  const [open, setOpen] =
    typeof props.open === 'undefined'
      ? // biome-ignore lint/correctness/useHookAtTopLevel: the following line is not pretty but it only breaks the rules of hooks if the component changes between controlled and uncontrolled.
        React.useState(props.defaultOpen ?? false)
      : [props.open, props.onOpenChange ?? (() => undefined)]
  return (
    <ModalContext.Provider value={setOpen}>
      <DialogPrimitive.Root {...props} open={open} onOpenChange={setOpen} />
    </ModalContext.Provider>
  )
}

const DialogPortal = DialogPrimitive.Portal
const DialogDescription = DialogPrimitive.Description

function DialogOverlay({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      className={twMerge(
        'fixed top-0 right-0 bottom-0 left-0 z-40 bg-black/50',
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Overlay>
  )
}

function DialogTrigger({
  className,
  children,
  Icon,
  Trigger,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger> & {
  Icon?: ElementType
  Trigger?: ReactNode
}) {
  return Boolean(Trigger) ? (
    <DialogPrimitive.Trigger ref={ref} asChild>
      {Trigger}
    </DialogPrimitive.Trigger>
  ) : (
    <DialogPrimitive.Trigger
      ref={ref}
      className={twMerge(
        'focus-visible:ring-ring ring-offset-background bg-primary hover:bg-primary-hover text-primary-contrast inline-flex h-full cursor-pointer items-center justify-center px-4 py-2 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {children}
      {Icon && <Icon className="h-5 w-5" />}
    </DialogPrimitive.Trigger>
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={twMerge('absolute top-0 left-0 p-4 heading-03', className)}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  onClose,
  closeButton = true,
  contentClassName,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  onClose?: () => void
  closeButton?: boolean
  contentClassName?: string
}) {
  return (
    <DialogPrimitive.Content
      className={twMerge(
        'fixed z-50 w-full overflow-clip bg-white p-4 pt-14 pb-20 sm:absolute sm:top-1/2 sm:left-1/2 sm:w-[30rem] sm:-translate-x-1/2 sm:-translate-y-1/2',
        className,
      )}
      {...props}
    >
      <>
        <div
          className={twMerge(
            'dialog-scrollable-content w-full overflow-x-hidden overflow-y-auto',
            contentClassName,
          )}
        >
          {children}
        </div>
        {closeButton && (
          <DialogPrimitive.Close
            className="absolute top-4 right-4 inline-flex items-center justify-center"
            onClick={onClose}
            title="Schließen"
            aria-label="Schließen"
          >
            <Close className="h-6 w-6" />
          </DialogPrimitive.Close>
        )}
      </>
    </DialogPrimitive.Content>
  )
}

function DialogAbort({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  const { pending } = useFormStatus()
  return (
    <DialogPrimitive.Close asChild {...props}>
      <Button
        type="button"
        size="xl"
        className={twMerge(
          'absolute bottom-0 left-0 mt-14 flex h-16 w-1/2 p-4 text-sm',
          className,
        )}
        {...props}
        disabled={pending || props.disabled}
      >
        {children}
      </Button>
    </DialogPrimitive.Close>
  )
}

function DialogClose({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return (
    <DialogPrimitive.Close
      className={twMerge(
        className,
        'button-secondary absolute bottom-0 left-0 mt-14 flex h-16 w-1/2 p-4',
      )}
      {...props}
    >
      <span className="text-sm">{children}</span>
    </DialogPrimitive.Close>
  )
}

function DialogSubmit({
  className,
  children,
  variant = 'cta',
  ...props
}: React.ComponentProps<typeof Button>) {
  const { pending } = useFormStatus()

  return (
    <Button
      type="submit"
      variant={variant}
      className={twMerge(
        'absolute right-0 bottom-0 mt-14 flex h-16 w-1/2 items-start p-4',
        className,
      )}
      {...props}
      disabled={pending || props.disabled}
    >
      {children}
    </Button>
  )
}

export {
  Dialog,
  DialogAbort,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogSubmit,
  DialogTitle,
  DialogTrigger,
}
